/**
 * Transferencias de Control de Kits — port de kit-stock/src/pages/Transfers.tsx.
 * Acciones contextuales según la Clapbase activa: origen envía/cancela,
 * destino confirma recepción; edición/borrado con descripción del efecto
 * sobre el stock (ajustes compensatorios en el backend).
 */

import { toastError } from '@/lib/toast-error';
import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Pencil, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { useKits } from './KitsModule';
import type { KitTransferStatus } from '@/../../shared/types';
import { useTranslation } from 'react-i18next';
import { usePagination } from '@/hooks/usePagination';

type TransferRow = {
  transferId: string;
  fromOfficeId: string;
  toOfficeId: string;
  fromOfficeName: string;
  toOfficeName: string;
  quantity: number;
  note?: string;
  status: KitTransferStatus;
  createdAt: string;
  sentAt?: string;
  receivedAt?: string;
};

const TRANSFER_STATUS_LABELS: Record<KitTransferStatus, string> = {
  pendiente: 'Pendiente',
  en_transito: 'En tránsito',
  recibida: 'Recibida',
  cancelada: 'Cancelada',
};

const TRANSFER_STATUS_COLORS: Record<KitTransferStatus, string> = {
  pendiente: 'bg-yellow-100 text-yellow-700',
  en_transito: 'bg-blue-100 text-blue-700',
  recibida: 'bg-green-100 text-green-700',
  cancelada: 'bg-red-100 text-red-700',
};

/**
 * Acciones según las oficinas que el usuario PUEDE operar (no según la Clapbase
 * activa del selector). Antes, con la transferencia a Medellín "en tránsito",
 * el botón "Confirmar recepción" solo aparecía si tenías Medellín seleccionada
 * — aunque fueras admin o manager del destino. El backend ya autoriza por
 * acceso (origen para enviar/cancelar, destino para recibir); esto lo espeja.
 */
function getAvailableActions(
  transfer: TransferRow,
  accessibleOfficeIds: string[],
  // `t` llega por parámetro: esto no es un componente, no puede usar el hook.
  t: (key: string) => string
): { action: KitTransferStatus; label: string }[] {
  const actions: { action: KitTransferStatus; label: string }[] = [];
  const canFrom = accessibleOfficeIds.includes(transfer.fromOfficeId);
  const canTo = accessibleOfficeIds.includes(transfer.toOfficeId);

  if (transfer.status === 'pendiente' && canFrom) {
    actions.push({ action: 'en_transito', label: t('kits.transfers.enviar') });
    actions.push({ action: 'cancelada', label: t('common.ui.cancel') });
  }

  if (transfer.status === 'en_transito' && canTo) {
    actions.push({ action: 'recibida', label: t('kits.transfers.confirmar_recepcion') });
  }

  if (transfer.status === 'pendiente' && canTo && !canFrom) {
    // El destino también puede cancelar una pendiente
    actions.push({ action: 'cancelada', label: t('common.ui.cancel') });
  }

  return actions;
}

function stockEffectDescription(t: TransferRow, newQty: number): string | null {
  const diff = newQty - t.quantity;
  if (diff === 0) return null;
  if (t.status === 'en_transito') {
    return diff > 0
      ? `Se restarán ${diff} kits más del stock de ${t.fromOfficeName}.`
      : `Se devolverán ${-diff} kits al stock de ${t.fromOfficeName}.`;
  }
  if (t.status === 'recibida') {
    return diff > 0
      ? `Se restarán ${diff} kits más de ${t.fromOfficeName} y se sumarán ${diff} a ${t.toOfficeName}.`
      : `Se devolverán ${-diff} kits a ${t.fromOfficeName} y se restarán ${-diff} de ${t.toOfficeName}.`;
  }
  return null;
}

function useInvalidateTransfers() {
  const utils = trpc.useUtils();
  return (fromOfficeId: string, toOfficeId: string) => {
    utils.kits.listTransfers.invalidate();
    utils.kits.listMovements.invalidate();
    utils.kits.listOffices.invalidate();
    utils.kits.getOfficeSummary.invalidate({ officeId: fromOfficeId });
    utils.kits.getOfficeSummary.invalidate({ officeId: toOfficeId });
  };
}

function EditTransferDialog({ transfer, onClose }: { transfer: TransferRow; onClose: () => void }) {
  const { t } = useTranslation();
  const { offices } = useKits();
  const invalidate = useInvalidateTransfers();
  const [quantity, setQuantity] = useState(String(transfer.quantity));
  const [note, setNote] = useState(transfer.note ?? '');
  const [fromOfficeId, setFromOfficeId] = useState(transfer.fromOfficeId);
  const [toOfficeId, setToOfficeId] = useState(transfer.toOfficeId);
  const updateMutation = trpc.kits.updateTransfer.useMutation();

  const isPendiente = transfer.status === 'pendiente';
  const activeOffices = offices.filter((o) => o.active);
  const qty = parseInt(quantity, 10);
  const qtyValid = Number.isInteger(qty) && qty >= 1;
  const sameOffice = fromOfficeId === toOfficeId;
  const effect = qtyValid ? stockEffectDescription(transfer, qty) : null;

  const onSave = () => {
    if (!qtyValid || sameOffice) return;
    updateMutation.mutate(
      {
        transferId: transfer.transferId,
        quantity: qty,
        note,
        ...(isPendiente ? { fromOfficeId, toOfficeId } : {}),
      },
      {
        onSuccess: () => {
          toast.success(`Transferencia #${transfer.transferId} actualizada`);
          invalidate(transfer.fromOfficeId, transfer.toOfficeId);
          onClose();
        },
        onError: (e) => toastError(e, 'common.toast.no_se_pudo_actualizar_la_transferencia'),
      }
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.transfers.editar_transferencia')}{transfer.transferId}</DialogTitle>
          <DialogDescription>
            {isPendiente
              ? t('kits.transfers.editar_pendiente_desc')
              : t('kits.transfers.editar_enviada_desc')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isPendiente && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('kits.common.desde')}</Label>
                <Select value={fromOfficeId} onValueChange={setFromOfficeId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {activeOffices.map((o) => (
                      <SelectItem key={o.officeId} value={o.officeId}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('kits.common.hacia')}</Label>
                <Select value={toOfficeId} onValueChange={setToOfficeId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {activeOffices.map((o) => (
                      <SelectItem key={o.officeId} value={o.officeId}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {sameOffice && (
            <p className="text-sm text-destructive">{t('kits.transfers.el_origen_y_el_destino_no')}</p>
          )}
          <div className="space-y-2">
            <Label htmlFor="transfer-qty">{t('kits.common.cantidad')}</Label>
            <Input
              id="transfer-qty"
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
            {effect && <p className="text-sm text-amber-600">{effect}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="transfer-note">{t('kits.common.nota')}</Label>
            <Textarea id="transfer-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.ui.cancel')}</Button>
          <Button onClick={onSave} disabled={!qtyValid || sameOffice || updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {t('common.ui.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteTransferDialog({ transfer, onClose }: { transfer: TransferRow; onClose: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateTransfers();
  const deleteMutation = trpc.kits.deleteTransfer.useMutation();

  let effect = 'La transferencia se eliminará definitivamente.';
  if (transfer.status === 'en_transito') {
    effect = `La transferencia está en tránsito: al eliminarla se devolverán ${transfer.quantity} kits al stock de ${transfer.fromOfficeName}.`;
  } else if (transfer.status === 'recibida') {
    effect = `La transferencia ya fue recibida: al eliminarla se devolverán ${transfer.quantity} kits a ${transfer.fromOfficeName} y se restarán ${transfer.quantity} de ${transfer.toOfficeName}.`;
  }

  const onConfirm = () => {
    deleteMutation.mutate(
      { transferId: transfer.transferId },
      {
        onSuccess: () => {
          toast.success(`Transferencia #${transfer.transferId} eliminada`);
          invalidate(transfer.fromOfficeId, transfer.toOfficeId);
          onClose();
        },
        onError: (e) => toastError(e, 'common.toast.no_se_pudo_eliminar_la_transferencia'),
      }
    );
  };

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('kits.transfers.eliminar_la_transferencia')}{transfer.transferId}?</AlertDialogTitle>
          <AlertDialogDescription>{effect} {t('kits.transfers.esta_accion_no_se_puede_deshacer')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.ui.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={deleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {t('common.ui.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function KitsTransfers() {
  const { t } = useTranslation();
  const { activeOfficeId, offices, isGlobal } = useKits();
  const invalidate = useInvalidateTransfers();
  const [editingTransfer, setEditingTransfer] = useState<TransferRow | null>(null);
  const [deletingTransfer, setDeletingTransfer] = useState<TransferRow | null>(null);

  const { data: transfers, isLoading } = trpc.kits.listTransfers.useQuery({
    officeId: isGlobal ? undefined : activeOfficeId ?? undefined,
  });

  const { pageItems: pageTransfers, footer: paginationFooter } = usePagination(transfers ?? [], 25);

  const transitionMutation = trpc.kits.transitionTransfer.useMutation();

  const handleTransition = (transferId: string, toStatus: KitTransferStatus, fromOfficeId: string, toOfficeId: string) => {
    transitionMutation.mutate({ transferId, toStatus }, {
      onSuccess: () => {
        toast.success(`Transferencia actualizada a ${TRANSFER_STATUS_LABELS[toStatus]}`);
        invalidate(fromOfficeId, toOfficeId);
      },
      onError: (e) => toastError(e, 'common.toast.no_se_pudo_actualizar_la_transferencia'),
    });
  };

  const accessibleIds = new Set(offices.map((o) => o.officeId));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('kits.common.transferencias')}</h1>
        <p className="text-muted-foreground mt-1">{t('kits.transfers.gestiona_las_transferencias_de_kits_entre')}</p>
      </div>

      <Card className="shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !transfers || transfers.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <p className="text-lg">{t('kits.transfers.no_hay_transferencias_registradas')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>{t('kits.common.desde')}</TableHead>
                  <TableHead>{t('kits.common.hacia')}</TableHead>
                  <TableHead>{t('kits.common.cantidad')}</TableHead>
                  <TableHead>{t('kits.common.estado')}</TableHead>
                  <TableHead>{t('kits.common.creada')}</TableHead>
                  <TableHead>{t('kits.common.enviada')}</TableHead>
                  <TableHead>{t('kits.common.recibida')}</TableHead>
                  <TableHead>{t('kits.common.nota')}</TableHead>
                  <TableHead className="text-right">{t('kits.common.acciones')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageTransfers.map((transfer) => {
                  const actions = getAvailableActions(transfer, [...accessibleIds], t);
                  // Editar/eliminar exigen acceso al origen en el server; se ocultan si no.
                  const canManage = isGlobal || accessibleIds.has(transfer.fromOfficeId);
                  return (
                    <TableRow key={transfer.transferId}>
                      <TableCell className="font-mono text-sm">#{transfer.transferId}</TableCell>
                      <TableCell className="font-medium">{transfer.fromOfficeName}</TableCell>
                      <TableCell className="font-medium">{transfer.toOfficeName}</TableCell>
                      <TableCell className="font-bold">{transfer.quantity}</TableCell>
                      <TableCell>
                        <Badge className={TRANSFER_STATUS_COLORS[transfer.status]}>{TRANSFER_STATUS_LABELS[transfer.status]}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{format(new Date(transfer.createdAt), 'dd MMM yyyy', { locale: es })}</TableCell>
                      <TableCell className="font-mono text-sm">{transfer.sentAt ? format(new Date(transfer.sentAt), 'dd MMM yyyy', { locale: es }) : '—'}</TableCell>
                      <TableCell className="font-mono text-sm">{transfer.receivedAt ? format(new Date(transfer.receivedAt), 'dd MMM yyyy', { locale: es }) : '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{transfer.note || '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {actions.map(({ action, label }) => (
                            <Button
                              key={action}
                              size="sm"
                              variant="outline"
                              onClick={() => handleTransition(transfer.transferId, action, transfer.fromOfficeId, transfer.toOfficeId)}
                              disabled={transitionMutation.isPending}
                            >
                              {label}
                            </Button>
                          ))}
                          {canManage && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingTransfer(transfer)}
                                title={t('kits.transfers.editar_transferencia_2')}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeletingTransfer(transfer)}
                                title={t('kits.transfers.eliminar_transferencia')}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="px-4 pb-3">{paginationFooter}</div>
          </div>
        )}
      </Card>

      {editingTransfer && <EditTransferDialog transfer={editingTransfer} onClose={() => setEditingTransfer(null)} />}
      {deletingTransfer && <DeleteTransferDialog transfer={deletingTransfer} onClose={() => setDeletingTransfer(null)} />}
    </div>
  );
}
